import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { X, Sparkles, FileSpreadsheet, Loader2 } from "lucide-react";

export default function NewDashboardDialog({ datasets, onClose, onCreated }) {
  const [title, setTitle] = useState("New Dashboard");
  const [picked, setPicked] = useState(new Set());
  const [busy, setBusy] = useState(false);

  const toggle = (id) => setPicked(prev => {
    const s = new Set(prev); if (s.has(id)) s.delete(id); else s.add(id); return s;
  });

  const create = async () => {
    if (!picked.size) return toast.error("Pick at least one dataset");
    if (!title.trim()) return toast.error("Give the dashboard a title");
    setBusy(true);
    try {
      const { data } = await api.post("/dashboards/auto-from-datasets", { title, dataset_ids: Array.from(picked) });
      toast.success(`Dashboard created with ${data.widgets.length} widgets`);
      onCreated(data);
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4" data-testid="new-dash-dialog">
      <div className="bg-white rounded-2xl border border-[var(--border)] max-w-lg w-full shadow-xl max-h-[90vh] overflow-y-auto scroll-thin">
        <div className="p-6 border-b border-[var(--border)] flex items-center justify-between">
          <div>
            <div className="label-caps mb-1"><Sparkles size={12} className="inline mr-1" /> New dashboard</div>
            <h2 className="font-display text-xl font-medium">Auto-generate from your data</h2>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-900"><X size={18} /></button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="label-caps block mb-2">Title</label>
            <input data-testid="nd-title" value={title} onChange={(e) => setTitle(e.target.value)} className="input-base" />
          </div>
          <div>
            <label className="label-caps block mb-2">Pick data sources ({picked.size} selected)</label>
            <div className="space-y-2 max-h-72 overflow-y-auto scroll-thin border border-[var(--border)] rounded-lg p-2">
              {datasets.map(d => {
                const on = picked.has(d.id);
                return (
                  <label key={d.id} data-testid={`nd-ds-${d.id}`} className={`flex items-center gap-3 p-2.5 rounded-lg cursor-pointer transition-colors ${on ? "bg-[var(--primary-soft)]" : "hover:bg-slate-50"}`}>
                    <input type="checkbox" checked={on} onChange={() => toggle(d.id)} className="w-4 h-4" />
                    <FileSpreadsheet size={16} className="text-[var(--primary)] flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-slate-900 truncate">{d.name}</div>
                      <div className="text-xs text-slate-500 mono">{d.row_count} rows · {d.columns?.length} cols · {d.kind || d.category}</div>
                    </div>
                  </label>
                );
              })}
              {datasets.length === 0 && <div className="text-xs text-slate-500 p-3">No datasets — upload data first.</div>}
            </div>
            <div className="text-xs text-slate-500 mt-2">PeopleIQ will scan each dataset's structure and auto-generate the right widgets.</div>
          </div>
        </div>
        <div className="p-6 border-t border-[var(--border)] flex justify-end gap-3 bg-slate-50/50">
          <button onClick={onClose} className="btn-ghost">Cancel</button>
          <button onClick={create} disabled={busy || !picked.size} data-testid="nd-create" className="btn-primary">
            {busy ? <><Loader2 size={14} className="animate-spin" /> Creating…</> : <><Sparkles size={14} /> Create dashboard</>}
          </button>
        </div>
      </div>
    </div>
  );
}
